import React, { useEffect, useState } from 'react'
import { useDashboardContext } from '../../../../contexts/NewDashboardContext';
import { alertV2, dataProcess, machineProfile } from '../../../../api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { Card, CardBody } from "../../../../components/card/card";
import { CardExpandToggler } from "../../../../components/card/card";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export const MachineHealthClassification = ({}) => {
  const { dateRangeOverview } = useDashboardContext();
  const [chartData, setChartData] = useState(null);

  // Get theme colors from CSS variables
  const getThemeColors = () => {
    const bodyBg = getComputedStyle(document.body)
      .getPropertyValue("--bs-body-bg")
      .trim();
    const textColor = getComputedStyle(document.body)
      .getPropertyValue("--bs-body-color")
      .trim();
    const gridColor = getComputedStyle(document.body)
      .getPropertyValue("--bs-border-color")
      .trim();
    const themeColor = getComputedStyle(document.body)
      .getPropertyValue("--bs-theme")
      .trim();

    return { bodyBg, textColor, gridColor, themeColor };
  };

  const { bodyBg, textColor, gridColor, themeColor } = getThemeColors();

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "top",
        labels: {
          color: textColor,
          usePointStyle: true,
          padding: 20,
        },
      },
      title: {
        display: true,
        text: "Health Classifications Over Time",
        color: textColor,
        font: {
          size: 14,
          weight: "bold",
        },
      },
      tooltip: {
        backgroundColor: bodyBg,
        titleColor: textColor,
        bodyColor: textColor,
        borderColor: gridColor,
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        ticks: {
          color: textColor,
          font: {
            size: 12,
          },
        },
        grid: {
          color: "#4b4b4b",
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Number of Classifications",
          color: textColor,
          font: {
            size: 12,
          },
        },
        ticks: {
          color: textColor,
          font: {
            size: 12,
          },
          stepSize: 1,
        },
        grid: {
          color: "#4b4b4b",
          drawBorder: false,
        },
      },
    },
  };

  async function getData() {
    try {
      const result = await machineProfile.listMachineHealth({
        start_date: dateRangeOverview?.startDate,
        end_date: dateRangeOverview?.endDate,
      });
      const body = await result.json();

      // Process the data to create chart data
      processChartData(body.data);
    } catch (error) {
      console.error("Error fetching alarm data:", error);
      // Use mock data for demonstration
      createMockChartData();
    }
  }

  const processChartData = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) {
      console.log("No valid API data, using mock data");
      createMockChartData();
      return;
    }

    // Group health classifications by month and status
    const monthlyHealthData = {};

    apiData.forEach((healthRecord) => {
      if (healthRecord.created_at) {
        const date = new Date(healthRecord.created_at);
        const monthKey = `${date.getFullYear()}-${String(
          date.getMonth() + 1
        ).padStart(2, "0")}`;
        const monthName = date.toLocaleDateString("en-US", { month: "long" });

        // Handle null status as 'Pending'
        const status = healthRecord.status || "Pending";

        if (!monthlyHealthData[monthKey]) {
          monthlyHealthData[monthKey] = {
            monthName: monthName,
            Acceptable: 0,
            Observe: 0,
            Alert: 0,
            Critical: 0,
            Pending: 0,
          };
        }

        // Increment count for the specific status
        if (monthlyHealthData[monthKey][status] !== undefined) {
          monthlyHealthData[monthKey][status]++;
        }
      }
    });

    console.log("Monthly health data grouped:", monthlyHealthData);

    // Sort by date and get the data
    const sortedMonths = Object.keys(monthlyHealthData).sort();

    // If no data, use mock data
    if (sortedMonths.length === 0) {
      console.log("No chart data generated, using mock data");
      createMockChartData();
      return;
    }

    const labels = [];
    const acceptableData = [];
    const observeData = [];
    const alertData = [];
    const criticalData = [];
    const pendingData = [];

    sortedMonths.forEach((monthKey) => {
      const monthData = monthlyHealthData[monthKey];
      labels.push(monthData.monthName);
      acceptableData.push(monthData.Acceptable);
      observeData.push(monthData.Observe);
      alertData.push(monthData.Alert);
      criticalData.push(monthData.Critical);
      pendingData.push(monthData.Pending);
    });

    console.log("Chart labels:", labels);
    console.log("Acceptable data:", acceptableData);
    console.log("Observe data:", observeData);
    console.log("Alert data:", alertData);
    console.log("Critical data:", criticalData);
    console.log("Pending data:", pendingData);
    const chartData = {
      labels: labels,
      datasets: [
        {
          label: "Acceptable",
          data: acceptableData,
          borderColor: "#AFEACD",
          backgroundColor: "#AFEACD",
          borderWidth: 1,
          pointRadius: 3,
          pointHoverRadius: 8,
          tension: 0.1,
        },
        {
          label: "Observe",
          data: observeData,
          borderColor: "#EAE4AF",
          backgroundColor: "#EAE4AF",
          borderWidth: 1,
          pointRadius: 3,
          pointHoverRadius: 8,
          tension: 0.1,
        },
        {
          label: "Alert",
          data: alertData,
          borderColor: "#FFBE7E",
          backgroundColor: "#FFBE7E",
          borderWidth: 1,
          pointRadius: 3,
          pointHoverRadius: 8,
          tension: 0.1,
        },
        {
          label: "Critical",
          data: criticalData,
          borderColor: "#FF779C",
          backgroundColor: "#FF779C",
          borderWidth: 1,
          pointRadius: 3,
          pointHoverRadius: 8,
          tension: 0.1,
        },
        // {
        //     label: 'Pending',
        //     data: pendingData,
        //     borderColor: '#C6C6C6',
        //     backgroundColor: '#C6C6C6',
        //     borderWidth: 1,
        //     pointRadius: 3,
        //     pointHoverRadius: 8,
        //     tension: 0.1
        // }
      ],
    };

    setChartData(chartData);
  };

  const createMockChartData = () => {
    const data = {
      labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
      datasets: [
        {
          label: "Acceptable",
          data: [],
          borderColor: "#AFEACD",
          backgroundColor: "#AFEACD",
          borderWidth: 1,
          pointRadius: 3,
          pointHoverRadius: 8,
          tension: 0.1,
        },
        {
          label: "Observe",
          data: [],
          borderColor: "#EAE4AF",
          backgroundColor: "#EAE4AF",
          borderWidth: 1,
          pointRadius: 3,
          pointHoverRadius: 8,
          tension: 0.1,
        },
        {
          label: "Alert",
          data: [],
          borderColor: "#FFBE7E",
          backgroundColor: "#FFBE7E",
          borderWidth: 1,
          pointRadius: 3,
          pointHoverRadius: 8,
          tension: 0.1,
        },
        {
          label: "Critical",
          data: [],
          borderColor: "#FF779C",
          backgroundColor: "#FF779C",
          borderWidth: 1,
          pointRadius: 3,
          pointHoverRadius: 8,
          tension: 0.1,
        },
        // {
        //     label: 'Pending',
        //     data: [0, 0, 1],
        //     borderColor: '#C6C6C6',
        //     backgroundColor: '#C6C6C6',
        //     borderWidth: 1,
        //     pointRadius: 3,
        //     pointHoverRadius: 8,
        //     tension: 0.1
        // }
      ],
    };
    setChartData(data);
  };

  useEffect(() => {
    getData();
  }, [dateRangeOverview]);

  // If no data is available yet, show loading
  if (!chartData) {
    return (
      <div
        style={{
          height: "300px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: bodyBg,
          borderRadius: "8px",
          border: `1px solid ${gridColor}`,
        }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Card>
        <CardBody>
          <div className="d-flex justify-content-end">
            <CardExpandToggler />
          </div>
          <div style={{ position: "relative", height: "370px", width: "100%" }}>
            <Line options={options} data={chartData} />
          </div>
        </CardBody>
      </Card>
    </div>
  );
};


